import { FC, Fragment, useState } from 'react';
import {
	Alert,
	Button,
	Col,
	DatePicker,
	Divider,
	Form,
	Input,
	Row,
	Select,
	Table,
	Upload,
	UploadFile,
} from 'antd';
import {
	FileSearchOutlined,
	EditOutlined,
	LoadingOutlined,
	UploadOutlined,
} from '@ant-design/icons';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import {
	CustomerBusinessDocumentForm,
	useFRMCustomerPage,
} from './CustomerPage';
import { FileData } from '@/libs/fleet-api/services/types';
import { useModals } from '@/components/modals/ModalProvider';
import { FleetFile } from '@/libs/fleet-api/fetcher';
import dayjs from 'dayjs';
import { useForm } from '@/libs/antd/form';
import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import rule from '@/libs/antd/rules';
import { DefaultOptionType } from 'antd/es/select';
import { useMutation } from '@tanstack/react-query';
import { uploader } from '@/libs/fleet-api/uploader';

const FRMCustomerBusiness: FC<{ onSubmit: () => void }> = ({ onSubmit }) => {
	const { openPreviewModal } = useModals();
	const { customerForm, businessForm, validationStatus, isSubmitting } =
		useFRMCustomerPage();

	return (
		<Fragment>
			<Form.Item shouldUpdate>
				{({ getFieldValue }) => {
					const dataSource =
						(getFieldValue(
							customerForm.fieldName('customerBusinessDocuments'),
						) as CustomerBusinessDocumentForm[]) ?? [];

					return (
						<Fragment>
							{!validationStatus.business && (
								<Alert
									message={
										<>
											Dokumen dengan tanda{' '}
											<span style={{ color: 'red' }}>*</span> wajib di upload.
										</>
									}
									type="warning"
									className="custom-alert"
									showIcon
								/>
							)}
							<Table
								pagination={false}
								bordered
								dataSource={dataSource.map((data, key) => {
									return { ...data, key };
								})}
								columns={[
									{
										title: 'Action',
										width: 1,
										align: 'center',
										render: (_, record, index) => (
											<div
												style={{
													padding: '0',
													display: 'flex',
													alignItems: 'center',
													gap: '20px',
												}}
											>
												<a
													onClick={(e) => {
														e.preventDefault();
														if (record.file?.id) {
															openPreviewModal(record.file as FleetFile);
														} else {
															alert('no document found');
														}
													}}
													style={
														record.file?.url
															? { display: 'block' }
															: {
																	pointerEvents: 'none',
																	color: '#bababa',
																	display: 'block',
																}
													}
												>
													<FileSearchOutlined />
												</a>
												<a
													style={{ display: 'block' }}
													onClick={(e) => {
														e.preventDefault();
														businessForm.setModalState(index);
														businessForm.form?.setFieldsValue(record);
													}}
												>
													<EditOutlined />
												</a>
											</div>
										),
									},
									{
										title: 'Jenis Dokumen',
										render: (_, record) => {
											return (
												<>
													{record.documentType}
													{record.isMandatory && (
														<span style={{ color: 'red' }}>*</span>
													)}
												</>
											);
										},
									},
									{
										title: 'No Dokumen',
										dataIndex: ['documentNo'],
									},
									{
										title: 'Penerbit',
										render: (_, record) => {
											return record.publisher?.value ?? '-';
										},
									},
									{
										title: 'Tanggal Dokumen',
										render: (_, record) => {
											if (!record.documentDate) return null;
											return dayjs(record.documentDate).format('DD/MM/YYYY');
										},
									},
									{
										title: 'Masa Berlaku',
										render: (_, record) => {
											return record.validityPeriod?.value ?? '-';
										},
									},
								]}
							/>
						</Fragment>
					);
				}}
			</Form.Item>

			<Button
				type="primary"
				style={{
					width: 200,
					display: 'block',
					marginLeft: 'auto',
					marginTop: 60,
				}}
				disabled={!validationStatus.business}
				loading={isSubmitting}
				onClick={() => onSubmit()}
			>
				{customerForm.value?.customerInformation?.customerType === 1
					? 'NEXT'
					: 'SAVE'}
			</Button>
		</Fragment>
	);
};

export default FRMCustomerBusiness;

export const useFRMCustomerBusinessForm = () => {
	const { form, value, fieldName } = useForm<CustomerBusinessDocumentForm>();
	const [modalState, setModalState] = useState<number>();

	return {
		form,
		value,
		fieldName,
		modalState,
		setModalState,
	};
};

export const FRMCustomerBusinessForm: FC<
	ReturnType<typeof useFRMCustomerBusinessForm>
> = (props) => {
	const { customerForm, customerId } = useFRMCustomerPage();
	const { form, value, fieldName, modalState, setModalState } = props;

	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	return (
		<Form
			{...ANTD_DEFAULT_PROPS.FORM}
			form={form}
			onFinish={(values) => {
				customerForm.form.setFieldValue(
					customerForm.fieldName('customerBusinessDocuments'),
					customerForm.value?.customerBusinessDocuments?.map(
						(doc, docIndex) => {
							if (modalState === docIndex) return values;
							return doc;
						},
					),
				);
				setModalState(undefined);
			}}
		>
			<FleetModal
				open={Boolean(typeof modalState === 'number')}
				width={940}
				closeIcon={false}
				keyboard={false}
				maskClosable={false}
				buttonPosition="end"
				okButtonProps={{
					disabled: uploadMutation.isPending,
				}}
				onOk={() => form?.submit()}
				onCancel={() => setModalState(undefined)}
				afterClose={() => form.resetFields()}
			>
				<FleetTitle>Edit Dokumen</FleetTitle>
				<Form.Item
					noStyle
					name={fieldName('id')}
				/>
				<Form.Item
					noStyle
					name={fieldName('publishers')}
				/>
				<Form.Item
					noStyle
					name={fieldName('validityPeriods')}
				/>
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item
							name={fieldName('documentType')}
							label="Jenis Dokumen"
							labelCol={{ span: 8 }}
							rules={[rule.required()]}
						>
							<Input.TextArea
								rows={5}
								disabled
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name={fieldName('documentNo')}
							label="No. Dokumen"
							labelCol={{ span: 8 }}
							rules={[rule.required()]}
						>
							<Input />
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item
							label="Penerbit"
							name={fieldName('publisher')}
							labelCol={{ span: 8 }}
							getValueFromEvent={(_, option) => option?.data}
							getValueProps={(value) => {
								if (!value) return {};
								return {
									value: {
										value: value.id,
										label: value.value,
									},
								};
							}}
						>
							<Select
								options={value?.publishers?.map((data) => {
									return {
										value: data.id,
										label: data.value,
										data,
									} as DefaultOptionType;
								})}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							label="Tanggal Dokumen"
							name={fieldName('documentDate')}
							labelCol={{ span: 8 }}
							getValueProps={(value) => ({
								value: value && dayjs(value),
							})}
							normalize={(value) =>
								value && dayjs(value).utc(true).toISOString()
							}
							rules={[rule.required()]}
						>
							<DatePicker
								disabledDate={(now) => now > dayjs().subtract(1, 'day')}
							/>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item
							label="Masa Berlaku"
							name={fieldName('validityPeriod')}
							labelCol={{ span: 8 }}
							getValueFromEvent={(_, option) => option?.data}
							getValueProps={(value) => {
								if (!value) return {};
								return {
									value: {
										value: value.id,
										label: value.value,
									},
								};
							}}
						>
							<Select
								options={value?.validityPeriods?.map((data) => {
									return {
										value: data.id,
										label: data.value,
										data,
									} as DefaultOptionType;
								})}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name={fieldName(['file'])}
							required
							label="Upload"
							labelCol={{ span: 8 }}
							getValueProps={(value) => {
								const file = value as FileData;
								return {
									fileList: file?.id
										? ([
												{
													uid: file.id,
													name: file.name,
													thumbUrl: file.url,
												},
											] as UploadFile[])
										: [],
								};
							}}
							normalize={({ file }: { file: UploadFile }) => {
								return {
									id: file.uid,
									format: file.type,
									name: file.name,
									url: file.thumbUrl,
								} as FileData;
							}}
							rules={[rule.required()]}
						>
							<Upload.Dragger
								listType="picture"
								accept=".pdf,.jpg,.jpeg,.png"
								maxCount={1}
								beforeUpload={() => false}
								onRemove={() => {
									form.setFieldValue(fieldName(['file']), null);
									return false;
								}}
								onChange={async ({ file }) => {
									const fileid = Date.now().toString();
									const filepath = customerId
										? `customer/${customerId}/business-document`
										: 'customer/temp/business-document';

									try {
										const url = await uploadMutation.mutateAsync({
											file: file as unknown as File,
											fileid,
											filepath,
										});

										form.setFieldValue(fieldName(['file']), {
											format: 'jpeg',
											id: fileid,
											name: file.name,
											path: filepath,
											url,
										});
									} catch {
										form.setFieldValue(fieldName(['file']), null);
									}
								}}
							>
								{uploadMutation.isPending ? (
									<LoadingOutlined
										style={{
											fontSize: 25,
											color: '#BABABA',
											display: 'block',
										}}
									/>
								) : (
									<UploadOutlined
										style={{
											fontSize: 25,
											color: '#BABABA',
											display: 'block',
										}}
									/>
								)}
							</Upload.Dragger>
						</Form.Item>
					</Col>
				</Row>
			</FleetModal>
		</Form>
	);
};
